import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { logoutLocal } from "@services/auth";
import {
  getIdleTimeoutMs,
  getLastActivityAt,
  markUserActivity,
} from "@utils/sessionActivity";
import { getAppPath, isPublicPath } from "@utils/appPath";

// Module-level guard — survives React StrictMode double-mount / effect remount.
let _isLoggingOut = false;

function hasAuthToken(): boolean {
  return !!localStorage.getItem("access_token");
}

function isTimedOut(): boolean {
  const last = getLastActivityAt();
  if (!last) return false;
  return Date.now() - last >= getIdleTimeoutMs();
}

const DEBUG_IDLE = true;

export function useSessionIdleTimeout(): void {
  const navigate = useNavigate();
  const lastWriteAtRef = useRef<number>(0);
  const idleCheckTimerRef = useRef<number | null>(null);
  const idleDeadlineTimerRef = useRef<number | null>(null);

  const performIdleLogout = useCallback(() => {
    if (_isLoggingOut) {
      if (DEBUG_IDLE) console.log("[IdleDebug] performIdleLogout(): already running, skipped");
      return;
    }
    _isLoggingOut = true;

    if (DEBUG_IDLE) {
      console.log("[IdleDebug] performIdleLogout() → navigating to /login", {
        now: new Date().toISOString(),
        lastActivityAt: getLastActivityAt(),
        timeoutMs: getIdleTimeoutMs(),
        pathname: window.location.pathname,
      });
    }

    // Clear storage first so route guards see no token.
    try { localStorage.clear(); } catch (_) {}
    try { sessionStorage.clear(); } catch (_) {}

    // Fire-and-forget audit cleanup.
    logoutLocal().catch(() => {});

    // Keep public pages publicly accessible, even if a stale/expired token was present.
    // This avoids forcing a login redirect on routes like /signup-request.
    const currentPath = getAppPath();
    if (!isPublicPath(currentPath)) {
      // Navigate via React Router — works correctly inside BrowserRouter.
      navigate("/login", { replace: true });
    }

    // Reset flag after short delay in case navigation doesn't unmount this hook.
    setTimeout(() => { _isLoggingOut = false; }, 3000);
  }, [navigate]);

  useEffect(() => {
    const clearDeadlineTimer = () => {
      if (idleDeadlineTimerRef.current !== null) {
        if (DEBUG_IDLE) console.log("[IdleDebug] clearDeadlineTimer()", { timerId: idleDeadlineTimerRef.current });
        window.clearTimeout(idleDeadlineTimerRef.current);
        idleDeadlineTimerRef.current = null;
      }
    };

    const scheduleDeadlineTimer = () => {
      clearDeadlineTimer();
      if (!hasAuthToken()) return;

      const now = Date.now();
      const last = getLastActivityAt() ?? now;
      const timeoutMs = getIdleTimeoutMs();
      const remaining = timeoutMs - (now - last);

      if (DEBUG_IDLE) {
        console.log("[IdleDebug] scheduleDeadlineTimer()", {
          nowIso: new Date(now).toISOString(),
          lastIso: new Date(last).toISOString(),
          timeoutMs,
          remainingMs: remaining,
          deadlineIso: new Date(last + timeoutMs).toISOString(),
        });
      }

      if (remaining <= 0) {
        if (DEBUG_IDLE) console.log("[IdleDebug] scheduleDeadlineTimer(): already timed out");
        performIdleLogout();
        return;
      }

      idleDeadlineTimerRef.current = window.setTimeout(() => {
        if (DEBUG_IDLE) {
          console.log("[IdleDebug] deadline timer fired", {
            fireNowIso: new Date().toISOString(),
            lastActivityAt: getLastActivityAt(),
            timeoutMs: getIdleTimeoutMs(),
          });
        }
        performIdleLogout();
      }, remaining + 100);
    };

    const writeThrottled = (now: number) => {
      if (now - lastWriteAtRef.current < 5_000) return;
      lastWriteAtRef.current = now;
      markUserActivity(now);
      if (DEBUG_IDLE) console.log("[IdleDebug] markUserActivity()", { nowIso: new Date(now).toISOString() });
      scheduleDeadlineTimer();
    };

    const handleAnyActivity = (event: Event) => {
      if (!hasAuthToken()) return;
      if (!event.isTrusted) return;
      // If already timed out, don't let stray input reset the clock.
      if (isTimedOut()) {
        if (DEBUG_IDLE) console.log("[IdleDebug] handleAnyActivity(): timed out, ignoring event and forcing logout", { type: event.type });
        performIdleLogout();
        return;
      }
      if (DEBUG_IDLE) console.log("[IdleDebug] handleAnyActivity()", { type: event.type, nowIso: new Date().toISOString() });
      writeThrottled(Date.now());
    };

    const events: Array<keyof WindowEventMap> = ["keydown", "pointerdown"];

    for (const evt of events) {
      window.addEventListener(evt, handleAnyActivity, { capture: true, passive: true });
    }
    window.addEventListener("session-activity", scheduleDeadlineTimer);

    // Seed last-activity on mount if missing.
    if (hasAuthToken()) {
      if (!getLastActivityAt()) markUserActivity(Date.now());
      scheduleDeadlineTimer();
    }

    // Periodic fallback check (cross-tab safety).
    const checkIdle = () => {
      if (!hasAuthToken()) return;

      if (!getLastActivityAt()) {
        if (DEBUG_IDLE) console.log("[IdleDebug] checkIdle(): no last activity, seeding");
        markUserActivity(Date.now());
        scheduleDeadlineTimer();
        return;
      }

      const now = Date.now();
      const last = getLastActivityAt()!;
      const timeoutMs = getIdleTimeoutMs();
      const elapsedMs = now - last;
      const timedOut = elapsedMs >= timeoutMs;

      if (DEBUG_IDLE) {
        console.log("[IdleDebug] checkIdle()", {
          nowIso: new Date(now).toISOString(),
          lastIso: new Date(last).toISOString(),
          timeoutMs,
          elapsedMs,
          timedOut,
        });
      }

      if (timedOut) performIdleLogout();
    };

    const interval = Math.min(Math.max(Math.floor(getIdleTimeoutMs() / 6), 15_000), 60_000);
    idleCheckTimerRef.current = window.setInterval(checkIdle, interval);

    return () => {
      for (const evt of events) {
        window.removeEventListener(evt, handleAnyActivity, { capture: true } as any);
      }
      window.removeEventListener("session-activity", scheduleDeadlineTimer);
      if (idleCheckTimerRef.current !== null) window.clearInterval(idleCheckTimerRef.current);
      clearDeadlineTimer();
    };
  }, [performIdleLogout]);
}
